import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from '../../../../hooks';
import proofkitFilemakerService from '../../../../services/proofkitFilemakerService';
import { ArrowLeft, Edit, Trash, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Label } from '../../../../components/ui';

const CONTACTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACTS || "contacts";

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState(null);

  const fetchContact = async () => {
    try {
      setLoading(true);
      const res = await proofkitFilemakerService.getRecord(CONTACTS_LAYOUT, id);
      setContact(res?.data || null);
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo cargar el contacto",
        variant: "destructive",
      });
      setContact(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContact();
  }, [id]);

  const contactData = contact?.fieldData;

  const getEstadoBadge = (estado) => {
    const v = String(estado || "").toLowerCase();
    if (!v) return <Badge variant="secondary">Sin estado</Badge>;
    if (v === "activo") return <Badge variant="default">Activo</Badge>;
    if (v === "inactivo") return <Badge variant="secondary">Inactivo</Badge>;
    if (v === "deshabilitado") return <Badge variant="destructive">Deshabilitado</Badge>;
    return <Badge variant="secondary">{estado}</Badge>;
  };

  const handleDeleteContact = async () => {
    if (!window.confirm("¿Estás seguro de eliminar este contacto?")) {
      return;
    }

    try {
      await proofkitFilemakerService.deleteRecord(CONTACTS_LAYOUT, id);
      toast({
        title: "Contacto eliminado",
        description: "El contacto fue eliminado exitosamente",
      });
      navigate("/campanas/contacts");
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el contacto",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/campanas/contacts")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
        </div>
        <div className="text-center py-8">Cargando contacto...</div>
      </div>
    );
  }

  if (!contactData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/campanas/contacts")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
        </div>
        <div className="text-center py-8 text-[var(--yool-error)]">Contacto no encontrado</div>
      </div>
    );
  }

  const fullName = `${contactData.nombre || ""} ${contactData.apellido || ""}`.trim();
  const fechaCreacion = contactData?.["MarcaFechaYHoraCreación"] || "";
  const fechaModificacion = contactData?.["MarcaFechaYHoraModificación"] || "";
  const modificadoPor = contactData?.["ModificadoPor"] || "";
  const fuente = contactData?.fuente || "";
  const refNombre = contactData?.referente_nombre || "";
  const refApellido = contactData?.referente_apellido || "";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate("/campanas/contacts")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/campanas/contacts/${id}/segments`)}>
            <Users className="h-4 w-4 mr-2" />
            Segmentos
          </Button>
          <Button variant="outline" onClick={() => navigate(`/campanas/contacts/${id}/edit`)}>
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
          <Button variant="destructive" onClick={handleDeleteContact}>
            <Trash className="h-4 w-4 mr-2" />
            Eliminar
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Información del Contacto</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground">Nombre</Label>
              <p className="font-medium text-lg">{fullName || "Sin nombre"}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Email</Label>
              <p className="font-medium">{contactData.email || "-"}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Fecha de Creación</Label>
              <p className="font-medium">{fechaCreacion || "-"}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Fecha de Modificación</Label>
              <p className="font-medium">{fechaModificacion || "-"}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Modificado Por</Label>
              <p className="font-medium">{modificadoPor || "-"}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Fuente</Label>
              <p className="font-medium">{fuente || "-"}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Referente</Label>
              <p className="font-medium">{`${refNombre} ${refApellido}`.trim() || "-"}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
